import React, { useState, useEffect } from 'react';
import { api } from './lib/api';
import { searchPcg, type PcgAccount } from './lib/pcg2026';

interface OperationEntryProps {
  entrepriseId: string;
  exerciceId: string;
  exerciceDebut?: string;
  exerciceFin?: string;
  onSuccess?: () => void;
}

interface OperationLine {
  id: string;
  compte_id: string;
  compte_libelle: string;
  debit_centimes: number;
  credit_centimes: number;
}

const OperationEntry: React.FC<OperationEntryProps> = ({
  entrepriseId,
  exerciceId,
  exerciceDebut,
  exerciceFin,
  onSuccess,
}) => {
  const [date, setDate] = useState(() => localStorage.getItem('saisie_last_date') || '');
  const [libelle, setLibelle] = useState('');
  const [lines, setLines] = useState<OperationLine[]>([
    { id: '1', compte_id: '', compte_libelle: '', debit_centimes: 0, credit_centimes: 0 },
    { id: '2', compte_id: '', compte_libelle: '', debit_centimes: 0, credit_centimes: 0 },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [compteSearch, setCompteSearch] = useState('');
  const [compteOptions, setCompteOptions] = useState<PcgAccount[]>([]);
  const [searchIndex, setSearchIndex] = useState<number | null>(null);

  useEffect(() => {
    if (compteSearch.length < 2 || searchIndex === null) {
      setCompteOptions([]);
      return;
    }
    setCompteOptions(searchPcg(compteSearch).slice(0, 30));
  }, [compteSearch, searchIndex]);

  const totalDebit = lines.reduce((sum, line) => sum + line.debit_centimes, 0);
  const totalCredit = lines.reduce((sum, line) => sum + line.credit_centimes, 0);
  const isBalanced = totalDebit === totalCredit;

  const addLine = () => {
    const newId = String(lines.length + 1);
    setLines([...lines, { id: newId, compte_id: '', compte_libelle: '', debit_centimes: 0, credit_centimes: 0 }]);
  };

  const updateLine = (id: string, updates: Partial<OperationLine>) => {
    setLines(lines.map(line => line.id === id ? { ...line, ...updates } : line));
  };

  const removeLine = (id: string) => {
    if (lines.length > 1) {
      setLines(lines.filter(line => line.id !== id));
    }
  };

  const handleCompteSelect = (lineId: string, account: PcgAccount) => {
    updateLine(lineId, { compte_id: account.numero, compte_libelle: account.libelle });
    setCompteOptions([]);
    setSearchIndex(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setShowSuccess(false);
    setSubmitting(true);

    try {
      if (!date) {
        throw new Error('La date est obligatoire');
      }
      if (!libelle.trim()) {
        throw new Error('Le libellé est obligatoire');
      }
      if (!isBalanced) {
        throw new Error('Les débits et crédits doivent être équilibrés');
      }
      if (lines.some(line => !line.compte_id)) {
        throw new Error('Tous les comptes PCG doivent être remplis');
      }

      const payload = {
        entreprise_id: entrepriseId,
        exercice_id: exerciceId,
        date: new Date(date).toISOString(),
        libelle: libelle.trim(),
        lignes: lines.map(line => ({
          compte_id: line.compte_id,
          debit_centimes: Math.round(line.debit_centimes),
          credit_centimes: Math.round(line.credit_centimes),
        })),
      };

      await api.transactions.create(payload);
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        resetForm();
      }, 1500);
      if (onSuccess) onSuccess();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setLibelle('');
    setLines([
      { id: '1', compte_id: '', compte_libelle: '', debit_centimes: 0, credit_centimes: 0 },
      { id: '2', compte_id: '', compte_libelle: '', debit_centimes: 0, credit_centimes: 0 },
    ]);
    setDate(new Date().toISOString().slice(0, 10));
  };

  const inputClass = 'w-full rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400';
  const labelClass = 'text-[10px] uppercase tracking-widest text-gray-400 font-semibold mb-1';

  return (
    <div className="max-w-4xl mx-auto">
      {/* Success feedback */}
      {showSuccess && (
        <div className="flex justify-center py-8">
          <span className="text-4xl text-green-500 animate-bounce">✓</span>
        </div>
      )}

      {!showSuccess && (
        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-5">
            {/* DATE */}
            <div>
              <label htmlFor="operation-date">
                <p className={labelClass}>Date</p>
              </label>
              <input
                id="operation-date"
                type="date"
                value={date}
                min={exerciceDebut || undefined}
                max={exerciceFin || undefined}
                onChange={(e) => {
                  setDate(e.target.value);
                  if (e.target.value) localStorage.setItem('saisie_last_date', e.target.value);
                }}
                required
                className={inputClass}
              />
            </div>

            {/* LIBELLÉ */}
            <div>
              <p className={labelClass}>Libellé</p>
              <input
                type="text"
                value={libelle}
                onChange={(e) => setLibelle(e.target.value)}
                placeholder="Ex: Emprunt bancaire"
                required
                className={inputClass}
              />
            </div>

            {/* OPERATION TABLE */}
            <div>
              <p className={labelClass}>Lignes d'écriture</p>
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-1/2">
                        Compte PCG
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                        Débit (€)
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                        Crédit (€)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((line, index) => (
                      <tr key={line.id} className="border-t border-gray-100">
                        <td className="px-4 py-3">
                          <div className="relative">
                            <input
                              type="text"
                              value={compteSearch && searchIndex === index ? compteSearch : `${line.compte_id} ${line.compte_libelle ? '— ' + line.compte_libelle : ''}`.trim()}
                              onChange={(e) => {
                                setCompteSearch(e.target.value);
                                setSearchIndex(index);
                              }}
                              onFocus={() => setSearchIndex(index)}
                              placeholder="Rechercher compte..."
                              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                            />
                              {compteOptions.length > 0 && searchIndex === index && (
                                <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                                {compteOptions.map((acc) => (
                                  <li
                                    key={acc.numero}
                                    onClick={() => handleCompteSelect(line.id, acc)}
                                    className="px-4 py-2 text-sm hover:bg-purple-50 cursor-pointer border-b border-gray-50 last:border-0"
                                  >
                                    <strong>{acc.numero}</strong> — {acc.libelle}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            step="1"
                            min="0"
                            value={line.debit_centimes || ''}
                            onChange={(e) => updateLine(line.id, { debit_centimes: Math.round(parseInt(e.target.value) || 0) })}
                            className="w-full px-3 py-2 text-sm text-right border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 pr-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            placeholder="0"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            step="1"
                            min="0"
                            value={line.credit_centimes || ''}
                            onChange={(e) => updateLine(line.id, { credit_centimes: Math.round(parseInt(e.target.value) || 0) })}
                            className="w-full px-3 py-2 text-sm text-right border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 pr-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            placeholder="0"
                          />
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t border-gray-200 bg-gray-50">
                      <td className="px-4 py-3 text-left">
                        {isBalanced ? (
                          <span className="text-green-600 font-bold text-sm">ÉQUILIBRÉE</span>
                        ) : (
                          <span className="text-red-600 font-bold text-sm">DÉSÉQUILIBRÉE</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">
                        €{(totalDebit / 100).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">
                        €{(totalCredit / 100).toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-3 flex justify-between">
                <button
                  type="button"
                  onClick={addLine}
                  className="px-3 py-2 bg-purple-100 text-purple-700 text-sm font-medium rounded-lg hover:bg-purple-200 transition-colors"
                >
                  + Ajouter
                </button>
                {lines.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeLine(lines[lines.length - 1].id)}
                    className="px-3 py-2 bg-red-100 text-red-700 text-sm font-medium rounded-lg hover:bg-red-200 transition-colors"
                  >
                    × Supprimer
                  </button>
                )}
              </div>
            </div>


          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={submitting || !isBalanced}
            className={`w-full text-white rounded-xl py-4 font-semibold text-sm disabled:opacity-40 transition-colors mt-2 ${
              isBalanced
                ? 'bg-purple-600 hover:bg-purple-700'
                : 'bg-gray-400'
            }`}
          >
            {submitting ? 'Enregistrement...' : 'Enregistrer »'}
          </button>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm mt-2">
              {error}
            </div>
          )}
        </form>
      )}
    </div>
  );
};

export default OperationEntry;